import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import {
    User,
    onAuthStateChanged,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    sendPasswordResetEmail,
    signOut,
    RecaptchaVerifier,
    signInWithPhoneNumber,
    ConfirmationResult
} from 'firebase/auth';
import { auth } from '../firebase';

interface AuthContextType {
    user: User | null;
    loading: boolean;
    signInEmail: (email: string, pass: string) => Promise<void>;
    signUpEmail: (email: string, pass: string) => Promise<void>;
    resetPassword: (email: string) => Promise<void>;
    setUpRecaptcha: (elementId: string) => void;
    signInPhone: (phoneNumber: string) => Promise<ConfirmationResult>;
    logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            setUser(currentUser);
            setLoading(false);
        });
        return unsubscribe;
    }, []);

    const signInEmail = async (email: string, pass: string) => {
        await signInWithEmailAndPassword(auth, email, pass);
    };

    const signUpEmail = async (email: string, pass: string) => {
        await createUserWithEmailAndPassword(auth, email, pass);
    };

    // Always resolves successfully unless the input is malformed or rate-limited,
    // so we don't reveal which emails are registered. Caller shows a generic
    // "if that email is registered..." message.
    const resetPassword = async (email: string) => {
        try {
            await sendPasswordResetEmail(auth, email);
        } catch (err: any) {
            if (err?.code === 'auth/user-not-found') return;
            throw err;
        }
    };

    const logout = async () => {
        await signOut(auth);
    };

    const setUpRecaptcha = (elementId: string) => {
        if (!window.recaptchaVerifier) {
            window.recaptchaVerifier = new RecaptchaVerifier(auth, elementId, {
                'size': 'invisible',
                'callback': () => {
                    // reCAPTCHA solved
                }
            });
        }
    };

    const signInPhone = async (phoneNumber: string) => {
        if (!window.recaptchaVerifier) {
            throw new Error("Recaptcha not initialized");
        }
        return await signInWithPhoneNumber(auth, phoneNumber, window.recaptchaVerifier);
    };

    return (
        <AuthContext.Provider value={{ user, loading, signInEmail, signUpEmail, resetPassword, logout, setUpRecaptcha, signInPhone }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

declare global {
    interface Window {
        recaptchaVerifier: RecaptchaVerifier;
    }
}
